import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, CreditCard, Settings, Shield, LogOut } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ThemeToggle } from '@/components/ThemeToggle';

interface NavEntry {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  roles?: string[]; // undefined = todos los roles autenticados
}

const NAV: NavEntry[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/clients', label: 'Clients', icon: Users },
  { to: '/billing', label: 'Billing', icon: CreditCard },
  { to: '/settings', label: 'Settings', icon: Settings },
  { to: '/admin/tenants', label: 'Admin', icon: Shield, roles: ['super_admin'] },
];

export function AppLayout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* ── Sidebar ─────────────────────────────────────────────────── */}
      <aside className="w-56 shrink-0 border-r bg-card flex flex-col">
        {/* Logo / Brand */}
        <div className="flex items-center gap-2 px-5 h-14 border-b">
          <div className="size-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
            <LayoutDashboard className="size-4" />
          </div>
          <span className="font-semibold">RepairPulse</span>
          <Badge variant="muted" className="ml-auto uppercase text-[10px]">
            {user?.role ?? '—'}
          </Badge>
        </div>

        {/* Nav links */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.filter(
            (entry) => !entry.roles || entry.roles.includes(user?.role ?? ''),
          ).map((entry) => {
            const Icon = entry.icon;
            return (
              <NavLink
                key={entry.to}
                to={entry.to}
                end={entry.to === '/dashboard'}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`
                }
              >
                <Icon className="size-4 shrink-0" />
                {entry.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom: theme + user + sign out */}
        <div className="border-t px-3 py-3 space-y-2">
          <div className="flex items-center justify-between px-1">
            <span className="text-xs text-muted-foreground truncate max-w-[120px]">
              {user?.firstName ?? ''} {user?.lastName ?? ''}
            </span>
            <ThemeToggle />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSignOut}
            className="w-full justify-start text-muted-foreground hover:text-foreground"
          >
            <LogOut className="size-4 mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* ── Main content ────────────────────────────────────────────── */}
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}
